import {
  IonBadge, IonButton, IonCard, IonCardContent, IonCardHeader, IonCardSubtitle,
  IonCardTitle, IonCheckbox, IonCol, IonContent, IonFooter, IonIcon, IonItem,
  IonInput, IonLabel, IonModal, IonSpinner, IonText, IonTitle, IonToast, IonToolbar,
} from '@ionic/react';
import { logoWhatsapp } from 'ionicons/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { isAllowedMetaMessageOrigin, loadMetaFacebookSdk } from '../../../../../Utils/MetaFacebookSdk';
import {
  requestService, whatsappMetaActivate, whatsappMetaDisable,
  whatsappMetaOnboardingComplete, whatsappMetaOnboardingSession, whatsappMetaStatus,
  whatsappMetaReviewAssets, whatsappMetaReviewMessage, whatsappMetaReviewTemplate, whatsappMetaTest,
} from '../../../../../Utils/Services';

type Props = { empresaId: string };
type MetaStatus = {
  available: boolean;
  status: string;
  activeChannel: 'baileys' | 'meta_cloud_api' | 'none';
  reviewDemoAvailable?: boolean;
  integration?: {
    status?: string;
    wabaId?: string;
    phoneNumberId?: string;
    displayPhoneNumber?: string;
    verifiedName?: string;
    platformType?: string;
    isOnBizApp?: boolean;
    lastError?: { code?: string; message?: string };
  };
};
type ReviewAssets = {
  graphApiVersion: string;
  permissions: string[];
  waba: { id: string; name?: string; accountReviewStatus?: string };
  phone: {
    id: string;
    displayPhoneNumber?: string;
    verifiedName?: string;
    status?: string;
    qualityRating?: string;
    platformType?: string;
  };
  templates: Array<{ id?: string; name: string; status?: string; category?: string; language?: string }>;
  checkedAt: string;
};
type ReviewResult = {
  ok: boolean;
  messageAccepted?: boolean;
  messageReference?: string;
  recipient?: string;
  template?: string | { id?: string; name: string; status?: string; category?: string; language?: string };
  sentAt?: string;
  createdAt?: string;
};
type SignupSession = {
  sessionId: string;
  state?: string;
  nonce?: string;
  publicConfig: { appId: string; graphApiVersion: string; loginOptions: Record<string, any> };
};
type ActiveFlow = SignupSession & {
  empresaId: string;
  code?: string;
  embeddedSignup?: Record<string, any>;
  completing?: boolean;
  timeoutId?: number;
};

function endpoint(template: string, empresaId: string) {
  return template.replace('{empresaId}', encodeURIComponent(empresaId));
}

function reviewRequestId(prefix: string) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

const WhatsAppMetaCard: React.FC<Props> = ({ empresaId }) => {
  const history = useHistory();
  const [status, setStatus] = useState<MetaStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [toast, setToast] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewAssets, setReviewAssets] = useState<ReviewAssets | null>(null);
  const [reviewRecipient, setReviewRecipient] = useState('');
  const [reviewResult, setReviewResult] = useState<ReviewResult | null>(null);
  const mounted = useRef(true);
  const flow = useRef<ActiveFlow | null>(null);

  const clearFlow = useCallback(() => {
    if (flow.current?.timeoutId) window.clearTimeout(flow.current.timeoutId);
    flow.current = null;
  }, []);

  const api = useCallback(<T,>(url: string, method: string, body?: unknown) => new Promise<T>((resolve, reject) => {
    requestService(url, {
      method,
      credentials: 'include',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }, history, (value: any) => resolve(value as T), reject, true);
  }), [history]);

  const refresh = useCallback(async () => {
    if (!empresaId) {
      setStatus(null);
      return;
    }
    try {
      const next = await api<MetaStatus>(endpoint(whatsappMetaStatus.url, empresaId), whatsappMetaStatus.method);
      if (mounted.current) setStatus(next);
    } catch (_) {
      if (mounted.current) setStatus(null);
    }
  }, [api, empresaId]);

  const completeIfReady = useCallback(async () => {
    const current = flow.current;
    if (!current || current.completing || !current.code || !current.embeddedSignup) return;
    if (current.empresaId !== empresaId) {
      clearFlow();
      setBusy(false);
      setToast('A empresa selecionada mudou. Inicie o cadastro novamente.');
      return;
    }
    current.completing = true;
    try {
      await api(endpoint(whatsappMetaOnboardingComplete.url, empresaId), whatsappMetaOnboardingComplete.method, {
        sessionId: current.sessionId,
        state: current.state || current.nonce,
        code: current.code,
        embeddedSignup: current.embeddedSignup,
      });
      clearFlow();
      await refresh();
      setConfirmed(false);
      setShowModal(true);
    } catch (error: any) {
      clearFlow();
      setToast(error?.message || 'Não foi possível concluir o Embedded Signup.');
    } finally {
      setBusy(false);
    }
  }, [api, clearFlow, empresaId, refresh]);

  useEffect(() => {
    mounted.current = true;
    clearFlow();
    setBusy(false);
    setShowModal(false);
    setShowReviewModal(false);
    setConfirmed(false);
    setReviewAssets(null);
    setReviewRecipient('');
    setReviewResult(null);
    refresh();
    return () => { mounted.current = false; clearFlow(); };
  }, [clearFlow, refresh]);

  useEffect(() => {
    const listener = (message: MessageEvent) => {
      if (!isAllowedMetaMessageOrigin(message.origin) || !flow.current) return;
      let payload = message.data;
      if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch (_) { return; }
      }
      if (payload?.type !== 'WA_EMBEDDED_SIGNUP') return;
      const eventName = payload.event || payload.data?.event;
      if (eventName === 'CANCEL' || eventName === 'ERROR') {
        clearFlow();
        setBusy(false);
        setToast(eventName === 'CANCEL' ? 'Cadastro cancelado.' : 'A Meta informou uma falha no cadastro.');
        return;
      }
      if (eventName !== 'FINISH' && eventName !== 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') return;
      const data = payload.data || {};
      flow.current.embeddedSignup = {
        ...data,
        wabaId: data.waba_id || data.wabaId,
        phoneNumberId: data.phone_number_id || data.phoneNumberId,
        businessId: data.business_id || data.businessId,
        onboardingType: 'whatsapp_business_app_onboarding',
        featureType: 'whatsapp_business_app_onboarding',
      };
      void completeIfReady();
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [clearFlow, completeIfReady]);

  const startSignup = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const session = await api<SignupSession>(endpoint(whatsappMetaOnboardingSession.url, empresaId), whatsappMetaOnboardingSession.method, {});
      flow.current = {
        ...session,
        empresaId,
        timeoutId: window.setTimeout(() => {
          clearFlow();
          setBusy(false);
          setToast('O tempo do cadastro expirou. Inicie novamente.');
        }, 5 * 60 * 1000),
      };
      const facebook = await loadMetaFacebookSdk(session.publicConfig.appId, session.publicConfig.graphApiVersion);
      facebook.login((result: any) => {
        if (!flow.current || flow.current.empresaId !== empresaId) return;
        const code = result?.authResponse?.code;
        if (!code) {
          clearFlow();
          setBusy(false);
          setToast('Popup fechado ou autorização cancelada.');
          return;
        }
        flow.current.code = code;
        void completeIfReady();
      }, session.publicConfig.loginOptions);
    } catch (error: any) {
      clearFlow();
      setBusy(false);
      setToast(error?.message || 'Não foi possível iniciar o Embedded Signup.');
    }
  };

  const activate = async () => {
    if (!confirmed || busy) return;
    setBusy(true);
    try {
      await api(endpoint(whatsappMetaActivate.url, empresaId), whatsappMetaActivate.method, { confirmouBaileysDesvinculado: true });
      await refresh();
      setShowModal(false);
      setToast('WhatsApp Oficial ativado.');
    } catch (error: any) {
      setToast(error?.message || 'Não foi possível ativar a integração.');
    } finally { setBusy(false); }
  };

  const testConnection = async () => {
    setBusy(true);
    try {
      await api(endpoint(whatsappMetaTest.url, empresaId), whatsappMetaTest.method, {});
      setToast('WABA, número e token validados com sucesso.');
    } catch (error: any) { setToast(error?.message || 'Falha ao validar a integração.'); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    const accepted = window.confirm('Desconectar a integração oficial do Xeguei? Isso não exclui a conta nem remove o número do WhatsApp Business.');
    if (!accepted) return;
    setBusy(true);
    try {
      await api(endpoint(whatsappMetaDisable.url, empresaId), whatsappMetaDisable.method, {});
      await refresh();
      setShowModal(false);
      setToast('Integração oficial desconectada.');
    } catch (error: any) { setToast(error?.message || 'Não foi possível desconectar a integração.'); }
    finally { setBusy(false); }
  };

  const loadReviewAssets = async () => {
    setReviewBusy(true);
    try {
      const result = await api<ReviewAssets>(endpoint(whatsappMetaReviewAssets.url, empresaId), whatsappMetaReviewAssets.method);
      setReviewAssets(result);
    } catch (error: any) {
      setToast(error?.message || 'Não foi possível consultar os ativos da Test WABA.');
    } finally { setReviewBusy(false); }
  };

  const openReviewDemo = () => {
    setReviewResult(null);
    setShowReviewModal(true);
    void loadReviewAssets();
  };

  const sendReviewMessage = async () => {
    if (!reviewRecipient.trim()) {
      setToast('Informe o destinatário autorizado no API Setup.');
      return;
    }
    setReviewBusy(true);
    setReviewResult(null);
    try {
      const result = await api<ReviewResult>(endpoint(whatsappMetaReviewMessage.url, empresaId), whatsappMetaReviewMessage.method, {
        requestId: reviewRequestId('message'),
        recipient: reviewRecipient,
      });
      setReviewResult(result);
      setReviewRecipient('');
      setToast('Mensagem hello_world aceita pela Meta. Confirme o recebimento no celular.');
    } catch (error: any) {
      setToast(error?.message || 'A Meta não concluiu o envio de demonstração.');
    } finally { setReviewBusy(false); }
  };

  const createReviewTemplate = async () => {
    const accepted = window.confirm('Criar um novo template Utility exclusivamente na Test WABA para a demonstração da App Review?');
    if (!accepted) return;
    setReviewBusy(true);
    setReviewResult(null);
    try {
      const result = await api<ReviewResult>(endpoint(whatsappMetaReviewTemplate.url, empresaId), whatsappMetaReviewTemplate.method, {
        requestId: reviewRequestId('template'),
      });
      setReviewResult(result);
      await loadReviewAssets();
      setToast('Template criado na Test WABA. Mostre o status retornado pela Meta.');
    } catch (error: any) {
      setToast(error?.message || 'A Meta não concluiu a criação do template.');
    } finally { setReviewBusy(false); }
  };

  if (!status?.available) return null;
  const connected = status.status === 'connected' && status.activeChannel === 'meta_cloud_api';
  const pending = status.status === 'configuration_pending';
  const integration = status.integration;

  return <>
    <IonCol size="12" sizeMd="6" sizeLg="6">
      <IonCard style={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <IonCardHeader style={{ position: 'relative' }}>
          {(connected || pending) && <IonBadge color={connected ? 'success' : 'warning'} style={{ position: 'absolute', right: 8, top: 8 }}>
            {connected ? 'Ativo' : 'Configuração pendente'}
          </IonBadge>}
          <IonIcon icon={logoWhatsapp} color="success" style={{ fontSize: 80 }} />
          <IonCardTitle>WhatsApp Oficial — Meta</IonCardTitle>
          <IonCardSubtitle>Meta Cloud API</IonCardSubtitle>
        </IonCardHeader>
        <IonCardContent style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <p>{integration?.verifiedName || 'Conecte o número atual pelo fluxo de Coexistência.'}</p>
          {integration?.displayPhoneNumber && <IonText color="medium"><p>Número: {integration.displayPhoneNumber}</p></IonText>}
          <IonButton size="large" disabled={busy} onClick={pending || connected ? () => setShowModal(true) : startSignup}>
            {busy ? <IonSpinner name="crescent" /> : pending || connected ? 'Configurar' : 'Ativar'}
          </IonButton>
          {status.reviewDemoAvailable && <IonButton fill="outline" color="tertiary" disabled={reviewBusy} onClick={openReviewDemo}>
            Demonstração da App Review
          </IonButton>}
        </IonCardContent>
      </IonCard>
    </IonCol>

    <IonModal isOpen={showModal} onDidDismiss={() => setShowModal(false)}>
      <IonToolbar color="primary"><IonTitle>WhatsApp Oficial</IonTitle></IonToolbar>
      <IonContent className="ion-padding">
        {(pending || connected) && <IonCard>
          <IonCardHeader><IonCardTitle>Dados da conexão</IonCardTitle></IonCardHeader>
          <IonCardContent>
            <p><strong>Status:</strong> {connected ? 'Ativo' : 'Configuração pendente'}</p>
            <p><strong>WABA ID:</strong> {integration?.wabaId || 'Não informado'}</p>
            <p><strong>Phone Number ID:</strong> {integration?.phoneNumberId || 'Não informado'}</p>
            <p><strong>Número:</strong> {integration?.displayPhoneNumber || 'Não informado'}</p>
            <p><strong>Plataforma:</strong> {integration?.platformType || 'Não informada'}</p>
            <p><strong>WhatsApp Business App:</strong> {integration?.isOnBizApp === true ? 'Coexistência confirmada' : 'Aguardando confirmação'}</p>
            {integration?.lastError?.message && <IonText color="danger"><p>{integration.lastError.message}</p></IonText>}
          </IonCardContent>
        </IonCard>}
        {pending && <>
          <h2>Última etapa</h2>
          <p>Para evitar respostas duplicadas, desconecte o dispositivo antigo utilizado pelo Xeguei em WhatsApp Business → Aparelhos conectados. Não exclua sua conta e não remova o número do WhatsApp Business.</p>
          <IonItem lines="none">
            <IonCheckbox slot="start" checked={confirmed} onIonChange={(event) => setConfirmed(event.detail.checked)} />
            <IonLabel className="ion-text-wrap">Confirmo que desconectei o dispositivo antigo do Xeguei/Baileys.</IonLabel>
          </IonItem>
          <IonButton expand="block" disabled={!confirmed || busy} onClick={activate}>Confirmar e ativar</IonButton>
        </>}
        {connected && <>
          <h2>Integração ativa</h2>
          <p>{integration?.verifiedName} {integration?.displayPhoneNumber}</p>
          <IonButton expand="block" disabled={busy} onClick={testConnection}>Validar conexão</IonButton>
          <IonButton expand="block" color="danger" fill="outline" disabled={busy} onClick={disable}>Desconectar do Xeguei</IonButton>
          <IonText color="medium"><p>Desconectar aqui não exclui a conta nem remove o número do WhatsApp Business.</p></IonText>
        </>}
      </IonContent>
      <IonFooter><IonToolbar><IonButton slot="end" fill="clear" onClick={() => setShowModal(false)}>Fechar</IonButton></IonToolbar></IonFooter>
    </IonModal>

    <IonModal isOpen={showReviewModal} onDidDismiss={() => setShowReviewModal(false)}>
      <IonToolbar color="tertiary"><IonTitle>Demonstração da App Review</IonTitle></IonToolbar>
      <IonContent className="ion-padding">
        <IonText color="medium">
          <p>Ambiente restrito à Test WABA. Estas ações não alteram o canal Baileys, não cadastram números e não usam ativos de produção.</p>
        </IonText>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Ativos e permissões</IonCardTitle>
            <IonCardSubtitle>whatsapp_business_management</IonCardSubtitle>
          </IonCardHeader>
          <IonCardContent>
            {reviewBusy && !reviewAssets ? <IonSpinner name="crescent" /> : reviewAssets ? <>
              <p><strong>Graph API:</strong> {reviewAssets.graphApiVersion}</p>
              <p><strong>Test WABA:</strong> {reviewAssets.waba.name || 'Test WhatsApp Business Account'} ({reviewAssets.waba.id})</p>
              <p><strong>Phone Number ID:</strong> {reviewAssets.phone.id}</p>
              <p><strong>Número:</strong> {reviewAssets.phone.displayPhoneNumber || 'Mascarado'}</p>
              <p><strong>Status:</strong> {reviewAssets.phone.status || 'Não informado'}</p>
              <p><strong>Qualidade:</strong> {reviewAssets.phone.qualityRating || 'Não informada'}</p>
              <p><strong>Plataforma:</strong> {reviewAssets.phone.platformType || 'Não informada'}</p>
              <p><strong>Templates encontrados:</strong> {reviewAssets.templates.length}</p>
              {reviewAssets.templates.slice(0, 10).map((template) => <p key={template.id || template.name}>
                <code>{template.name}</code> — {template.status || 'SEM STATUS'} — {template.language || 'sem idioma'}
              </p>)}
            </> : <p>Carregue os ativos da Test WABA para iniciar a demonstração.</p>}
            <IonButton expand="block" fill="outline" disabled={reviewBusy} onClick={loadReviewAssets}>
              Atualizar ativos e templates
            </IonButton>
            <IonButton expand="block" color="tertiary" disabled={reviewBusy || !reviewAssets} onClick={createReviewTemplate}>
              Criar template de demonstração
            </IonButton>
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Enviar hello_world</IonCardTitle>
            <IonCardSubtitle>whatsapp_business_messaging</IonCardSubtitle>
          </IonCardHeader>
          <IonCardContent>
            <IonItem>
              <IonLabel position="stacked">Destinatário autorizado no API Setup</IonLabel>
              <IonInput
                type="password"
                value={reviewRecipient}
                placeholder="Informe com DDI"
                onIonChange={(event) => setReviewRecipient(String(event.detail.value || ''))}
              />
            </IonItem>
            <IonText color="medium"><p>O destinatário não é salvo e precisa estar na allowlist segura do backend.</p></IonText>
            <IonButton expand="block" color="success" disabled={reviewBusy || !reviewAssets} onClick={sendReviewMessage}>
              {reviewBusy ? <IonSpinner name="crescent" /> : 'Enviar mensagem de teste'}
            </IonButton>
          </IonCardContent>
        </IonCard>

        {reviewResult && <IonCard color="light">
          <IonCardHeader><IonCardTitle>Resultado sanitizado</IonCardTitle></IonCardHeader>
          <IonCardContent>
            {reviewResult.messageAccepted && <>
              <p><strong>Mensagem aceita:</strong> Sim</p>
              <p><strong>Template:</strong> {typeof reviewResult.template === 'string' ? reviewResult.template : reviewResult.template?.name}</p>
              <p><strong>Destinatário:</strong> {reviewResult.recipient}</p>
              <p><strong>Referência:</strong> {reviewResult.messageReference}</p>
              <p>Agora mostre a mensagem recebida no celular.</p>
            </>}
            {typeof reviewResult.template === 'object' && <>
              <p><strong>Template criado:</strong> {reviewResult.template.name}</p>
              <p><strong>ID:</strong> {reviewResult.template.id || 'Aguardando retorno'}</p>
              <p><strong>Status:</strong> {reviewResult.template.status || 'PENDING'}</p>
              <p><strong>Categoria:</strong> {reviewResult.template.category}</p>
            </>}
          </IonCardContent>
        </IonCard>}
      </IonContent>
      <IonFooter><IonToolbar><IonButton slot="end" fill="clear" onClick={() => setShowReviewModal(false)}>Fechar</IonButton></IonToolbar></IonFooter>
    </IonModal>
    <IonToast isOpen={Boolean(toast)} message={toast} duration={5000} onDidDismiss={() => setToast('')} />
  </>;
};

export default WhatsAppMetaCard;
